import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, PencilIcon } from 'lucide-react';
import { buttonOutlineLightClass, buttonPrimaryClass } from '../../utils/formStyles';
import { urls } from '../../data/site';
import type { QuoteValues } from '../../types/quote';
interface QuoteConfirmationProps {
  values: QuoteValues;
  fileCount: number;
  onReview: () => void;
}
export function QuoteConfirmation({
  values,
  fileCount,
  onReview
}: QuoteConfirmationProps) {
  const reduce = useReducedMotion();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  const summary = [{
    label: 'Name',
    value: values.name
  }, {
    label: 'Service',
    value: values.service || 'Not selected'
  }, {
    label: 'Vehicle',
    value: values.vehicle || 'Not provided'
  }, {
    label: 'Files attached',
    value: fileCount ? String(fileCount) : 'None'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: reduce ? 0 : 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.24,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-272" data-mp="2t4z7cqn26vmv">
      <span aria-hidden="true" className="inline-flex h-12 w-12 items-center justify-center bg-ink text-paper" data-id="element-273" data-mp="2x8dnriewte0u">
        <CheckIcon className="h-6 w-6" strokeWidth={2.5} data-id="element-274" data-mp="374gp4m1xt8mq" />
      </span>
      <h2 ref={headingRef} tabIndex={-1} className="mt-6 font-display text-[36px] font-bold uppercase leading-[0.95] text-ink md:text-[48px]" data-id="element-275" data-mp="373p4u7dsixbr">
        Quote form preview complete
      </h2>
      <p className="mt-4 max-w-[34rem] border-l-[3px] border-brand pl-4 text-[17px] leading-relaxed text-ink" data-id="element-276" data-mp="2edgie3c81qrd">
        This is a client preview. No request has been sent.
      </p>

      <dl className="mt-8 grid border-t border-line sm:grid-cols-2" data-id="element-277" data-mp="2o6xghjje5s0u">
        {summary.map(row => <div key={row.label} className="min-w-0 border-b border-line py-4 sm:pr-6" data-id="element-278" data-mp="1qo4e9hvr48vx">
            <dt className="text-[12px] font-semibold uppercase tracking-[0.14em] text-steel" data-id="element-279" data-mp="1vngz5x32b5vv">{row.label}</dt>
            <dd className="mt-1 truncate text-[16px] text-ink" title={row.value} data-id="element-280" data-mp="1nmj23vmmf8os">
              {row.value}
            </dd>
          </div>)}
      </dl>

      <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-281" data-mp="1xddoczzva8a8">
        <a href={urls.home} className={`${buttonPrimaryClass} w-full sm:w-auto`} data-id="element-282" data-mp="1sfc5af2fy8yr">
          <ArrowLeftIcon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" data-id="element-283" data-mp="1shom2owgqda8" />
          <span data-id="element-284" data-mp="14q1e9paw7sxt">Back to home</span>
        </a>
        <button type="button" onClick={onReview} className={`${buttonOutlineLightClass} w-full sm:w-auto`} data-id="element-285" data-mp="10oo638kbvdnq">
          <PencilIcon aria-hidden="true" className="h-[16px] w-[16px] shrink-0" data-id="element-286" data-mp="1eo7du0988mzi" />
          <span data-id="element-287" data-mp="19p47mchigapy">Review form</span>
        </button>
      </div>
    </motion.div>;
}